import { z } from 'zod';
import { LIMITS } from '../constants.js';

/**
 * Contrats d'authentification, partages entre le frontend et l'API.
 *
 * Les memes schemas servent a valider la saisie cote navigateur (retour
 * immediat a l'utilisateur) et a valider la requete cote serveur (seule
 * validation qui fait foi).
 */

/** Mots de passe trop courants, refuses quelle que soit leur longueur. */
const COMMON_PASSWORDS = new Set([
  'motdepasse',
  'password',
  'password123',
  'azertyuiop',
  'qwertyuiop',
  '1234567890',
  'motdepasse1',
  'administrateur',
  'firegame123',
  'canadair123',
  'jetaime123',
  'bonjour123',
  'soleil123',
  'football123',
]);

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(LIMITS.emailMax, 'Adresse e-mail trop longue.')
  .pipe(z.email('Adresse e-mail invalide.'));

export const passwordSchema = z
  .string()
  .min(
    LIMITS.passwordMin,
    `Le mot de passe doit contenir au moins ${LIMITS.passwordMin} caracteres.`,
  )
  .max(LIMITS.passwordMax, 'Mot de passe trop long.')
  .refine(
    (value) => !COMMON_PASSWORDS.has(value.toLowerCase()),
    'Ce mot de passe est trop courant. Choisissez-en un autre.',
  )
  .refine(
    (value) => /[a-zA-Z]/.test(value) && /[0-9]/.test(value),
    'Le mot de passe doit contenir au moins une lettre et un chiffre.',
  );

export const displayNameSchema = z
  .string()
  .trim()
  .min(LIMITS.displayNameMin, 'Le pseudonyme doit contenir au moins 2 caracteres.')
  .max(LIMITS.displayNameMax, 'Pseudonyme trop long.')
  .regex(
    /^[\p{L}\p{N} _.'-]+$/u,
    "Le pseudonyme ne peut contenir que des lettres, chiffres, espaces et les signes . _ ' -",
  );

export const registerRequestSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: displayNameSchema,
});

export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Mot de passe requis.').max(LIMITS.passwordMax),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const forgotPasswordRequestSchema = z.object({
  email: emailSchema,
});

export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>;

export const resetPasswordRequestSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});

export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(LIMITS.passwordMax),
  newPassword: passwordSchema,
});

export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

export const USER_ROLES = ['PLAYER', 'ADMIN'] as const;
export type UserRoleId = (typeof USER_ROLES)[number];

/** Representation publique d'un utilisateur : jamais de hash ni de jeton. */
export const publicUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: z.enum(USER_ROLES),
  locale: z.string(),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
});

export type PublicUser = z.infer<typeof publicUserSchema>;

export const userSettingsSchema = z.object({
  volumeMaster: z.number().int().min(0).max(100),
  volumeMusic: z.number().int().min(0).max(100),
  volumeSfx: z.number().int().min(0).max(100),
  volumeAmbience: z.number().int().min(0).max(100),
  reducedEffects: z.boolean(),
  highContrast: z.boolean(),
  showHints: z.boolean(),
  uiScale: z.number().int().min(80).max(150),
});

export type UserSettings = z.infer<typeof userSettingsSchema>;

/** Mise a jour partielle des parametres personnels. */
export const updateSettingsRequestSchema = userSettingsSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'Aucun parametre a mettre a jour.');

export type UpdateSettingsRequest = z.infer<typeof updateSettingsRequestSchema>;

/** Reponse de `/auth/me` : session courante ou visiteur anonyme. */
export const sessionResponseSchema = z.object({
  user: publicUserSchema.nullable(),
  settings: userSettingsSchema.nullable(),
});

export type SessionResponse = z.infer<typeof sessionResponseSchema>;

/** Reponse commune aux actions sans donnees de retour. */
export const okResponseSchema = z.object({
  ok: z.literal(true),
  message: z.string().optional(),
});

export type OkResponse = z.infer<typeof okResponseSchema>;

/** Nom du cookie contenant le jeton anti-CSRF, lu par le frontend. */
export const CSRF_COOKIE_NAME = 'fg_csrf';
/** En-tete dans lequel le frontend renvoie le jeton anti-CSRF. */
export const CSRF_HEADER_NAME = 'x-csrf-token';
